/**
 * Industry rate card section (contracts-plan.md §7.7, §11 tab 3; §13.5
 * WP-44). Upload a CSV or xlsx of other-site rates with a source name, date
 * and descriptor, matched to entries through §7.5 and stored as
 * cta_industry_datapoints; shown as a range beside WCR's.
 *
 * MOUNT CONTRACT. This replaces WP-41's "Industry rate card" PanelSlot in
 * CardView.tsx (done, forbidden to this WP) — see
 * backend/src/routes/contracts/industry.mount.md's "Frontend mount" section
 * for the exact one-line import and JSX the orchestrator applies at the
 * MOUNT step. Until then this component is reachable only through its own
 * test (rateCard.industry.test.tsx), which renders it directly.
 *
 * PROPS CONTRACT (stated here, per the orchestrator's brief, and in
 * services/contracts/industry/README.md's frontend note):
 *   - `entryId?: string` — omitted (the CardView placeholder slot, item 5 of
 *     industry.mount.md): only the upload form and its own result summary
 *     render. Given (a per-row hook into WP-41's EntryRowView — NOT wired by
 *     this WP; see industry.mount.md item 6, [OWNER]): a compact "WCR
 *     low-high (n)" / "Industry low-high (n)" comparison ALSO renders, above
 *     the form, from GET /industry?entryId — §7.7's own words, "shown as a
 *     separate range beside WCR's".
 *   - NO `siteId` prop. cta_industry_datapoints (§5.7) carries no site_id
 *     column — the table is network-scoped only (R17: "other-site rates",
 *     never this network's own site data), and the network comes from the
 *     guard's req.contracts.networkId server-side, same as every other
 *     contracts route. A siteId prop here would be dead weight; this is a
 *     deliberate departure from the orchestrator brief's suggested shape
 *     ({ entryId?, siteId? }), recorded in this WP's report NOTES.
 */
import { useEffect, useState, FormEvent, ChangeEvent } from 'react';
import { primaryBtnCls, fieldCls, useToast } from '../../../../ui';
import { uploadIndustryFile, getIndustryRange } from '../../../api/industry';
import type { IndustryRangeResult, UnmatchedIndustryRow, UploadIndustryResult } from '../../../api/industry';

interface Props {
  /** Show the WCR-vs-industry range comparison for this one entry (see header). */
  entryId?: string;
}

function RangeComparison({ entryId }: { entryId: string }) {
  const [range, setRange] = useState<IndustryRangeResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    getIndustryRange(entryId)
      .then((result) => {
        if (!cancelled) setRange(result);
      })
      .catch(() => {
        if (!cancelled) setRange(null);
      });
    return () => {
      cancelled = true;
    };
  }, [entryId]);

  if (!range) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Loading range…</p>;
  }

  const wcrText = `WCR ${range.wcr.low ?? '—'}–${range.wcr.high ?? '—'} (${range.wcr.count})`;
  const industryText = `Industry ${range.industry.low ?? '—'}–${range.industry.high ?? '—'} (${range.industry.count})`;

  return (
    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700 dark:text-gray-300" data-testid="industry-range-comparison">
      <span data-testid="industry-range-wcr">{wcrText}</span>
      <span data-testid="industry-range-industry">{industryText}</span>
    </div>
  );
}

export function IndustrySection({ entryId }: Props) {
  const { toast } = useToast();
  const [sourceName, setSourceName] = useState('');
  const [sourceDate, setSourceDate] = useState('');
  const [siteDescriptor, setSiteDescriptor] = useState('');
  const [region, setRegion] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<UploadIndustryResult | null>(null);

  const canSubmit = sourceName.trim().length > 0 && sourceDate.trim().length > 0 && !!file && !submitting;

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit || !file) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const uploaded = await uploadIndustryFile(file, {
        sourceName: sourceName.trim(),
        sourceDate: sourceDate.trim(),
        siteDescriptor: siteDescriptor.trim() || undefined,
        region: region.trim() || undefined,
      });
      setResult(uploaded);
      toast(
        `Stored ${uploaded.stored.length} row(s)${uploaded.unmatched.length > 0 ? `; ${uploaded.unmatched.length} need matching.` : '.'}`,
        { type: uploaded.unmatched.length > 0 ? 'info' : 'success' }
      );
    } catch (err) {
      const res = (err as { response?: { data?: { error?: string } } })?.response;
      setFormError(res?.data?.error ?? 'Something went wrong uploading this file.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-3" data-testid="industry-section">
      {entryId && <RangeComparison entryId={entryId} />}

      <form onSubmit={handleSubmit} className="space-y-3">
        {formError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {formError}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="industry-source-name" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
              Source name
            </label>
            <input
              id="industry-source-name"
              type="text"
              value={sourceName}
              onChange={(e) => setSourceName(e.target.value)}
              className={`${fieldCls} w-full`}
            />
          </div>
          <div>
            <label htmlFor="industry-source-date" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
              Source date
            </label>
            <input
              id="industry-source-date"
              type="date"
              value={sourceDate}
              onChange={(e) => setSourceDate(e.target.value)}
              className={`${fieldCls} w-full`}
            />
          </div>
          <div>
            <label htmlFor="industry-descriptor" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
              Site descriptor (optional)
            </label>
            <input
              id="industry-descriptor"
              type="text"
              value={siteDescriptor}
              onChange={(e) => setSiteDescriptor(e.target.value)}
              className={`${fieldCls} w-full`}
            />
          </div>
          <div>
            <label htmlFor="industry-region" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
              Region (optional)
            </label>
            <input
              id="industry-region"
              type="text"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className={`${fieldCls} w-full`}
            />
          </div>
        </div>

        <div>
          <label htmlFor="industry-file" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
            File (CSV or xlsx)
          </label>
          <input
            id="industry-file"
            type="file"
            accept=".csv,.xlsx"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-700 dark:text-gray-300"
          />
        </div>

        <button type="submit" data-testid="industry-upload-submit" disabled={!canSubmit} className={primaryBtnCls}>
          {submitting ? 'Uploading…' : 'Upload'}
        </button>
      </form>

      {result && (
        <div className="space-y-2 text-sm" data-testid="industry-upload-result">
          <p data-testid="industry-stored-count">Stored {result.stored.length} row(s).</p>
          {result.unmatched.length > 0 && (
            <div data-testid="industry-unmatched-list">
              <p className="font-medium text-gray-700 dark:text-gray-200">{result.unmatched.length} row(s) need matching:</p>
              <ul className="list-disc pl-5">
                {result.unmatched.map((row: UnmatchedIndustryRow) => (
                  <li key={row.row} data-testid={`industry-unmatched-${row.row}`}>
                    Row {row.row}: {row.label || '(no label)'} — {row.value ?? '(no value)'}
                    {row.reason ? ` (${row.reason})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default IndustrySection;
